import styled from "styled-components";

import { i18n, Translations } from "@i18n";
import { SerifTitle } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";

/**
 * Serif headline with one phrase emphasised. On web the spec's emphasis is italic copper via <em>
 * (SerifTitle styles it), rather than the SemiBold the mobile design uses — the two apps share
 * the copy but each speaks in its own typographic voice.
 */
export const MigrationHeadline = ({ text, highlight }: { text: string; highlight: string }) => {
  const index = text.indexOf(highlight);

  if (index === -1) {
    return <Headline $size={24}>{text}</Headline>;
  }

  return (
    <Headline $size={24}>
      {text.slice(0, index)}
      <em>{highlight}</em>
      {text.slice(index + highlight.length)}
    </Headline>
  );
};

const Headline = styled(SerifTitle)`
  align-self: stretch;
  line-height: 32px;
  letter-spacing: 0;
`;

export const MigrationReassurance = ({ items }: { items: string[] }) => (
  <List $gap={11}>
    {items.map((item) => (
      <Row key={item}>
        <Tick aria-hidden>✓</Tick>
        <RowLabel>{item}</RowLabel>
      </Row>
    ))}
  </List>
);

export type MigrationStep = { title: string; subtitle: string };

export const MigrationSteps = ({ steps }: { steps: MigrationStep[] }) => (
  <List as="ol" $gap={16}>
    {steps.map((step, index) => (
      <Step key={step.title}>
        <StepNumber>{String(index + 1).padStart(2, "0")}</StepNumber>
        <StepCopy>
          <StepTitle>{step.title}</StepTitle>
          <StepSubtitle>{step.subtitle}</StepSubtitle>
        </StepCopy>
      </Step>
    ))}
  </List>
);

/** Why the goal may look different next — shown whichever way the user reached this point. */
export const MigrationBalanceNotice = () => (
  <Notice>
    <NoticeTitle>{i18n.t(Translations.CapitalGroupMigrationBalanceNoticeTitle)}</NoticeTitle>
    <NoticeBody>{i18n.t(Translations.CapitalGroupMigrationBalanceNoticeBody)}</NoticeBody>
  </Notice>
);

const List = styled.ul<{ $gap: number }>`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Tick = styled.span`
  flex: none;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid ${home.sage};
  background: ${home.sageChipBg};
  font-size: 10px;
  font-weight: 600;
  color: ${home.sage};
`;

const RowLabel = styled.span`
  font-size: 13px;
  line-height: 19px;
  color: ${home.ink80};
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.span`
  flex: none;
  font-size: 13px;
  font-weight: 600;
  line-height: 21px;
  color: ${home.copperText};
`;

const StepCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StepTitle = styled.span`
  font-size: 14.5px;
  font-weight: 500;
  line-height: 21px;
  color: ${home.ink};
`;

const StepSubtitle = styled.span`
  font-size: 12.5px;
  line-height: 18px;
  color: ${home.ink52};
`;

const Notice = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${home.line08};
  background: ${home.fill06};
`;

const NoticeTitle = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  line-height: 18px;
  color: ${home.ink};
`;

const NoticeBody = styled.span`
  font-size: 12.5px;
  line-height: 18px;
  color: ${home.ink62};
`;
